import {
  Controller,
  Get,
  Post,
  Param,
  Body,
  Patch,
  UseGuards,
} from '@nestjs/common';
import { SalesService } from './sales.service';
import {
  CreateSalesDto,
  UpdateSalesDto,
  CreateSaleAttachmentsDto,
  UpdateSaleAttachmentsDto,
} from '@crm/dto';
import { ApiResponseInterface, SalesStatus, UserRole } from '@crm/interface';
import { AuthGuard } from '../guards/auth.guard';
import { RolesGuard } from '../guards/roles.guard';
import { Roles } from '../guards/roles.decorator';
import { IsEnum, IsOptional, IsString, IsUUID } from 'class-validator';

class UpdateSaleStatusDto {
  @IsUUID()
  id!: string;

  @IsEnum(SalesStatus)
  status!: SalesStatus;

  @IsOptional()
  @IsString()
  updated_by_id?: string;
}

@Controller('sales')
export class SalesController {
  constructor(private readonly salesService: SalesService) {}

  // ====== GET ALL SALES ======
  @Get()
  async getAll(): Promise<ApiResponseInterface> {
    return this.salesService.getAll();
  }

  // ====== GET SALES FOR SUPERVISOR ======
  @Get('supervisor')
  async getForSupervisor(): Promise<ApiResponseInterface> {
    return this.salesService.getForSupervisor();
  }

  // ====== GET SALES BY USER ID ======
  @Get('user/:userId')
  async getByUserId(@Param('userId') userId: string): Promise<ApiResponseInterface> {
    return this.salesService.getByUserId(userId);
  }

  // ====== GET SALE BY ID ======
  @Get(':id')
  async getById(@Param('id') id: string): Promise<ApiResponseInterface> {
    return this.salesService.getById(id);
  }

  // ====== CREATE SALE ======
  @Post()
  async create(@Body() dto: CreateSalesDto): Promise<ApiResponseInterface> {
    return this.salesService.create(dto);
  }

  // ====== UPDATE STATUS (management + supervisor only) ======
  @Patch('update-status')
  @UseGuards(AuthGuard, RolesGuard)
  @Roles(UserRole.MANAGEMENT, UserRole.SUPERVISOR)
  async updateStatus(@Body() dto: UpdateSaleStatusDto): Promise<ApiResponseInterface> {
    return this.salesService.updateStatus(dto.id, dto.status, dto.updated_by_id);
  }

  // ====== UPDATE SALE (all roles) ======
  @Patch()
  async update(@Body() dto: UpdateSalesDto): Promise<ApiResponseInterface> {
    return this.salesService.update(dto);
  }

  // ====== SOFT DELETE SALE ======
  @Patch('soft-delete')
  async softDelete(@Body() dto: UpdateSalesDto): Promise<ApiResponseInterface> {
    return this.salesService.softDelete(dto);
  }

  // ====== CREATE SALE ATTACHMENT ======
  @Post('attachments')
  async createAttachment(@Body() dto: CreateSaleAttachmentsDto): Promise<ApiResponseInterface> {
    return this.salesService.createSalesAttachment(dto);
  }

  // ====== UPDATE SALE ATTACHMENT ======
  @Patch('attachments')
  async updateAttachment(@Body() dto: UpdateSaleAttachmentsDto): Promise<ApiResponseInterface> {
    return this.salesService.updateSalesAttachment(dto);
  }
}
